import { motion } from '@carbon/motion';
import {
  desktop1920pxOrWider,
  tabletOrNarrower,
  tabletOrWider,
} from '@upright/ui/common/generic/layout/breakpoint-utils';
import { ReactNode, CSSProperties, useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import styled, { css } from 'styled-components';
import { getTextStyle } from '../content/texts/bou-text/text-style';
import { BouColor } from '../global/bou-colors';
import { MotionDuration, MotionMode, MotionName } from '../motion/types';
import { CloseButton } from './styles';

export interface PlainModalProps {
  isOpen: boolean;
  closeModal: () => void;
  children: ReactNode;
  accessibleName: string;
  modalId: string; // Needed to create a unique parent element for the modal
  hideCloseButton?: boolean;

  // This prop overrides all the modal styling. If you don't want to override but want to extend existing styling, either
  // export the style functions below to be used with this prop, or check the comment for "className" below.
  style?: CSSProperties;

  // This enables using styled(PlainModal)``;, which is handy if you just want to add to the default style instead of overriding it.
  // Remember to use the css classNames in your styles (for example .ReactModal__Content {}).
  className?: string;

  // "down" by default. Use 'up' only when doing canceling etc. stuff that indicates that "we go back" instead of forward
  exit?: 'up' | 'down';

  // JO: set to true if you wish to self-manage creating the parent for the modal.
  // NOTE OA: If you use this, PlainModal will search the DOM for an element that has props.modalId as its id,
  // so make sure to have such an element in the DOM if you use this prop. Also, if you refactor the parent attachment
  // to use refs instead of id's, please refactor the places where the parents are added to DOM to provide refs as a
  // prop for this component.
  doNotCreateModalParent?: boolean;
}

/**
 * A plain modal component that applies minimal styles and is somewhat protected from accidental
 * surprise styling from global css. Please try not to bloat this component with custom stuff, instead
 * do your needed things in a different component that uses this one.
 *
 * TODO: Nice defaults for margins for the content area for different display sizes.
 *
 */

export const PlainModal = (props: PlainModalProps) => {
  const modalId = props.modalId;
  const createModalParent = props.doNotCreateModalParent !== true;

  // OA: modalId === Parent element ID. This naming is a bit confusing. Also, this should be done with a ref instead of
  // document selector. However, doing that change requires refactoring places where props.doNotCreateModalParent === true,
  // so is not a quick fix.
  const parentDomElement = document.querySelector<HTMLElement>(`#${props.modalId}`);
  const isModalParentInDOM = !!parentDomElement;
  useRerenderIfParentNotPresent(props.isOpen, createModalParent, isModalParentInDOM);

  // If we create a modal parent, we should wait for the parent's DOM node to be present
  // before rendering ReactModal. This boolean fill force only the parent element to be rendered
  // on the first render. That render then triggers a rerender where we also render the ReactModal,
  // having the parent node present in DOM.
  const shouldRenderReactModal = !createModalParent || !!parentDomElement;

  return (
    <>
      {shouldRenderReactModal && (
        <ReactModal
          isOpen={props.isOpen}
          onRequestClose={props.closeModal}
          shouldCloseOnOverlayClick={true}
          closeTimeoutMS={MotionDuration.MODERATE2_240ms}
          // We choose a custom parent element for the modal in order to to give custom
          // stylings via the ModalParent component below. Without doing this, the modal would be
          // rendered right below <body>, and couldn't be styled with Styled Components.
          parentSelector={parentDomElement ? () => parentDomElement : undefined}
          // Providing *any* classNames stops react-modal from force-applying default inline styles to "content"
          // and "overlay" components. So we give some names to be able to handle all the styling
          // using css in ModalParent.
          className={'_'}
          overlayClassName={'_'}
          id={`${modalId}-content`}
          // PLT-6267: names the dialog itself, not just its close button. `accessibleName` used to
          // reach only the `aria-label` below, so the `role="dialog"` element react-modal renders
          // had no accessible name at all and announced as an unnamed dialog. Both existing hosts
          // pass a name already, so this costs them nothing and fixes them too — though note they
          // pass the modal id rather than a name a person would recognise.
          contentLabel={props.accessibleName}
        >
          {!props.hideCloseButton && (
            <CloseButton aria-label={`Close modal: ${props.accessibleName}`} onClick={props.closeModal}>
              &times;
            </CloseButton>
          )}
          {props.children}
        </ReactModal>
      )}
      {/* When the modal is open, it will actually be rendered under this element in the DOM */}
      {createModalParent && (
        <ModalParent id={modalId} style={props.style} className={props.className} $exit={props.exit || 'down'} />
      )}
    </>
  );
};

interface ModalParentProps {
  $exit: 'up' | 'down';
}

export const ModalParent = styled.div<ModalParentProps>`
  ${props => getDefaultModalStyle(props.$exit)}
`;

/**
 * OA: To attach itself to a custom place in the DOM, ReactModal requires "parentSelector" prop, a
 * function that tells ReactModal which DOM node will be its parent. Using that prop we
 * tell ReactModal that the DOM node stored in parentDomElement is the chosen parent.
 *
 * When props.doNotCreateModalParent === false, we also render the parent component (ModalParent) in the the return clause of
 * PlainModal, i.e. in *the same component* where we call ReactModal. On the very first render of
 * PlainModal, ModalParent has not yet been rendered --> ReactModal will be given null as the parent DOM node, and will use <body> as its parent.
 *
 * This hook checks if we're creating ModalParent within PlainModal, and if so, will force a rerender after the very first render so that the parent node will
 * be appended to the DOM.
 */
function useRerenderIfParentNotPresent(
  shouldModalBeOpen: boolean,
  isPlainModalCreatingItsOwnParent: boolean,
  isParentNodePresentInDom: boolean,
) {
  const [, setUnusedState] = useState({});
  function forceReactRerender() {
    setUnusedState({});
  }
  useEffect(() => {
    if (isPlainModalCreatingItsOwnParent && shouldModalBeOpen && !isParentNodePresentInDom) forceReactRerender();
  }, [shouldModalBeOpen, isPlainModalCreatingItsOwnParent, isParentNodePresentInDom]);
}

function getDefaultModalStyle(exit: 'up' | 'down') {
  return [...getModalOverlayStyle(), ...getModalContentStyle(exit)];
}

function getModalOverlayStyle() {
  return css`
    .ReactModal__Overlay {
      background-color: rgba(0, 0, 0, 0.6);
      z-index: 300 !important;
      position: fixed;
      top: 0;
      left: 0;
      height: 100vh;
      width: 100vw;

      /* For transitions */
      opacity: 0;
    }

    /* This is the actual state that happens when the modal is open */
    .ReactModal__Overlay--after-open {
      opacity: 1;
      transition: all ${MotionDuration.MODERATE2_240ms}ms ${motion(MotionName.ENTRANCE, MotionMode.EXPRESSIVE)};
    }

    /* This is the target state for the exit animation */
    .ReactModal__Overlay--before-close {
      opacity: 0;
      transition: all ${MotionDuration.MODERATE2_240ms}ms ${motion(MotionName.EXIT, MotionMode.EXPRESSIVE)};
    }
  `;
}

function getModalContentStyle(exit: 'up' | 'down') {
  const beforeOpen = '-20%';
  const afterClose = exit === 'up' ? beforeOpen : '20%';
  return css`
    .ReactModal__Content {
      ${getTextStyle({ size: 'xsmall' })}
      position: fixed;
      inset: 0;
      margin: auto;
      width: fit-content;
      height: fit-content;
      max-width: 90vw;
      max-height: 95vh;
      overflow-y: auto;
      outline: none;
      border-radius: 8px;
      border: 1px solid ${BouColor.PLATFORM_BORDER_GRAY};
      z-index: 301 !important;

      ${tabletOrNarrower(css`
        max-height: 88vh;
      `)}
      ${tabletOrWider(css`
        max-width: min(860px, 90vw);
      `)}
      ${desktop1920pxOrWider(css`
        max-width: 1020px;
      `)}

      /* This applies only to iOS devices, which tend to overlay some
         nav bar stuff over the content. */
      @supports (-webkit-touch-callout: none) {
        max-height: 80vh;
      }

      /* For transitions */
      opacity: 0;
      transform: translateY(${beforeOpen});
    }

    /* This is the actual state that happens when the modal is open */
    .ReactModal__Content--after-open {
      opacity: 1;
      transform: translateY(0%);
      transition: all ${MotionDuration.MODERATE2_240ms}ms ${motion(MotionName.ENTRANCE, MotionMode.EXPRESSIVE)};
    }

    /* This is the target state for the exit animation */
    .ReactModal__Content--before-close {
      opacity: 0;
      transform: translateY(${afterClose});
      /* The faster animation here is on purpose */
      transition: all ${MotionDuration.MODERATE1_150ms}ms ${motion(MotionName.EXIT, MotionMode.EXPRESSIVE)};
    }
  `;
}
